import React, { useMemo, useEffect } from 'react'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Typography, Alert } from '@mui/material'
import { LightBulbIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { PageWrapper } from '../../../shared/components'
import { Spinner, ButtonSecondaryUpgraded } from '../../../interaxDesignSystemComponents'
import { GamerFeedbackContainerBox } from '../../feedbackGenerator/components/GamerFeedbackContainerBox'
import { ProfileCardWithIndicators } from '../../feedbackGenerator/components/ProfileCardWithIndicators'
import { FeedbackEmptyState } from '../../feedbackGenerator/components/FeedbackEmptyState'
import { GamerFeedbackSummaryTextContainer } from '../../feedbackGenerator/components/GamerFeedbackSummaryTextContainer'
import { PersonalityTraitsVisualization } from './components/PersonalityTraitsVisualization'
import { MyProfileSubSectionCard } from './components/MyProfileSubSectionCard'
import { ProfileGoalsCard } from './ariaCoach/components/ProfileGoalsCard'
import { useUserProfileReport } from '../../../apiLayer/queryHooks/queries'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import {
  transformTopStrengthsToProfileName,
  transformTopStrengthsToIndicators,
  generateProfileDescription,
  transformPersonalityScoresToTraits,
  transformDevelopmentPlanData,
  getVariableLabel
} from './helpers/myProfileHelpers'
import { transformUserAchievementsToAchievements } from '../achievements/helpers/userAchievementsTransformer'
import { isAchievementCompleted } from '../achievements/helpers/achievementHelpers'
import { AchievementBadge } from '../achievements/components/AchievementBadge'
import { formatFeedbackText } from '../../feedbackGenerator/helpers/textFormatter'
import DOMPurify from 'dompurify'
import type { SectionTab } from './components/MyProfileSubSectionCard'
import {
  PROFILE_FEEDBACK_BOX_IDS,
  ProfileFeedbackBoxId,
  useProfileFeedbackCovers
} from './hooks/useProfileFeedbackCovers'

/**
 * MyProfileGamerPage - Collaboration Profile page
 *
 * Displays user's collaboration profile with development plan sections
 */
export const MyProfileGamerPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  // Handle scroll to section based on hash in URL
  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.substring(1) // Remove the #
      const element = document.getElementById(hash)
      if (element) {
        // Small delay to ensure page is rendered
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    }
  }, [location.hash])

  // Fetch user profile report data
  const { data: profileReportResponse, isLoading, isError, error } = useUserProfileReport()

  const profileReportData = profileReportResponse?.data
  const isReady = profileReportData?.ready ?? false

  // Transform data for display
  const profileName = useMemo(() => {
    if (!profileReportData?.topStrengths) return ''
    return transformTopStrengthsToProfileName(profileReportData.topStrengths, t)
  }, [profileReportData?.topStrengths, t])

  const profileIndicators = useMemo(() => {
    if (!profileReportData?.topStrengths) return []
    return transformTopStrengthsToIndicators(profileReportData.topStrengths, t)
  }, [profileReportData?.topStrengths, t])

  const profileDescription = useMemo(() => {
    if (!profileReportData?.topStrengths) return ''
    return generateProfileDescription(profileReportData.topStrengths, t)
  }, [profileReportData?.topStrengths, t])

  const personalityTraits = useMemo(() => {
    if (!profileReportData?.personality?.scores) return []
    return transformPersonalityScoresToTraits(profileReportData.personality.scores, t)
  }, [profileReportData?.personality?.scores, t])

  const developmentPlanData = useMemo(() => {
    if (!profileReportData) {
      return {
        whatIsWorking: {},
        improvementAreas: {},
        tryThisAtWork: {}
      }
    }
    return transformDevelopmentPlanData(profileReportData, t)
  }, [profileReportData, t])

  // Format text content
  const formattedProfileSummary = useMemo(() => {
    if (!profileReportData?.profileSummary) return ''
    return DOMPurify.sanitize(formatFeedbackText(profileReportData.profileSummary))
  }, [profileReportData?.profileSummary])

  const formattedSelfPerceivedPersonality = useMemo(() => {
    if (!profileReportData?.selfPerceivedPersonality) return ''
    return DOMPurify.sanitize(formatFeedbackText(profileReportData.selfPerceivedPersonality))
  }, [profileReportData?.selfPerceivedPersonality])

  // Prepare tabs for sections
  const whatIsWorkingTabs: SectionTab[] = useMemo(() => {
    const areas = profileReportData?.whatIsWorking?.areas
    if (!areas || areas.length === 0) return []
    const tabs: SectionTab[] = []
    areas.forEach((area, index: number) => {
      // Use variable field if available, otherwise fallback to indexed label
      const label = area.variable
        ? getVariableLabel(area.variable, t, 'strengths')
        : t(`gamer.feedback.deeperAnalysis.tabs.strength${index + 1}`, `Strength ${index + 1}`)

      tabs.push({
        label,
        key: `section_${index}`
      })
    })
    return tabs
  }, [profileReportData?.whatIsWorking, t])

  const improvementAreasTabs: SectionTab[] = useMemo(() => {
    const areas = profileReportData?.improvementAreas?.areas
    if (!areas || areas.length === 0) return []
    const tabs: SectionTab[] = []
    areas.forEach((area, index: number) => {
      // Use variable field if available, otherwise fallback to indexed label
      const label = area.variable
        ? getVariableLabel(area.variable, t, 'weaknesses')
        : t(`gamer.feedback.deeperAnalysis.tabs.improvementArea${index + 1}`, `Improvement ${index + 1}`)

      tabs.push({
        label,
        key: `section_${index}`
      })
    })
    return tabs
  }, [profileReportData?.improvementAreas, t])

  const tryThisAtWorkTabs: SectionTab[] = useMemo(() => {
    if (!profileReportData?.tryThisAtWork) return []
    return profileReportData.tryThisAtWork.map((_, index) => ({
      label: t('gamer.collaborationProfile.tryThisAtWork.tabExample', {
        number: index + 1,
        defaultValue: `Example ${index + 1}`
      }),
      key: `example_${index}`
    }))
  }, [profileReportData?.tryThisAtWork, t])

  const shownFeedbackBoxIds = useMemo(() => {
    const boxIds: ProfileFeedbackBoxId[] = []
    if (whatIsWorkingTabs.length > 0) boxIds.push(PROFILE_FEEDBACK_BOX_IDS.whatsWorking)
    if (improvementAreasTabs.length > 0) boxIds.push(PROFILE_FEEDBACK_BOX_IDS.watchOut)
    return boxIds
  }, [whatIsWorkingTabs.length, improvementAreasTabs.length])

  const feedbackCovers = useProfileFeedbackCovers(shownFeedbackBoxIds)

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spinner />
        </Box>
      </PageWrapper>
    )
  }

  // Error state
  if (isError || !profileReportResponse?.success) {
    return (
      <PageWrapper>
        <Alert severity="error" sx={{ maxWidth: '800px', mx: 'auto' }}>
          {error?.message || profileReportResponse?.error || t('gamer.collaborationProfile.errors.loadingFailed')}
        </Alert>
      </PageWrapper>
    )
  }

  // Empty or not ready state
  if (!profileReportData || !isReady) {
    return (
      <PageWrapper>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '1400px', mx: 'auto' }}>
          {/* Header */}
          <Box>
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.headline3,
                color: INTERAX_COLORS.white,
                marginBottom: '8px'
              }}
            >
              {t('gamer.collaborationProfile.title')}
            </Typography>
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body2,
                color: INTERAX_COLORS.thunderGrey
              }}
            >
              {t('gamer.collaborationProfile.description')}
            </Typography>
          </Box>

          {/* Show development goals even before a development plan exists (self-hides if no goals). */}
          <ProfileGoalsCard />

          <GamerFeedbackContainerBox variant="team">
            <FeedbackEmptyState
              titleKey="gamer.collaborationProfile.errors.notReady"
              descriptionKey="gamer.collaborationProfile.developmentPlan.emptyState"
            />
          </GamerFeedbackContainerBox>
        </Box>
      </PageWrapper>
    )
  }

  // Main content
  return (
    <PageWrapper>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '1400px', mx: 'auto' }}>
        {/* Header */}
        <Box>
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.headline3,
              color: INTERAX_COLORS.white,
              marginBottom: '8px'
            }}
          >
            {t('gamer.collaborationProfile.title')}
          </Typography>
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.body2,
              color: INTERAX_COLORS.thunderGrey
            }}
          >
            {t('gamer.collaborationProfile.description')}
          </Typography>
        </Box>

        {/* Development goals set with Aria (only shown when the coach is enabled + goals exist) */}
        <ProfileGoalsCard />

        {/* Development Plan Section */}
        <GamerFeedbackContainerBox variant="team" sx={{ width: '100%' }}>
          {/* Section Header with Icon */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              padding: '0px',
              gap: '16px',
              width: '100%'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '0px',
                width: '48px',
                height: '48px',
                background: INTERAX_COLORS.sun20,
                borderRadius: '10px',
                flex: 'none'
              }}
            >
              <LightBulbIcon
                style={{
                  width: '24px',
                  height: '24px',
                  color: INTERAX_COLORS.sun
                }}
              />
            </Box>
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.headline3,
                color: INTERAX_COLORS.white,
                flex: 1,
                alignSelf: 'center'
              }}
            >
              {t('gamer.collaborationProfile.developmentPlan.title')}
            </Typography>
          </Box>

          {/* Row 1: Profile Overview */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '24px',
              width: '100%'
            }}
          >
            {/* Left: Profile Card with Indicators */}
            {profileIndicators.length > 0 && (
              <ProfileCardWithIndicators
                profileName={profileName}
                description={profileDescription}
                indicators={profileIndicators}
              />
            )}

            {/* Right: Profile Summary */}
            {formattedProfileSummary && (
              <GamerFeedbackSummaryTextContainer title={t('gamer.collaborationProfile.profileSummary.title')}>
                <Typography
                  sx={{
                    ...INTERAX_TYPOGRAPHY.body3,
                    color: INTERAX_COLORS.typographyFont,
                    margin: 0,
                    '& strong': {
                      fontWeight: 600,
                      color: INTERAX_COLORS.typographyFont
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: formattedProfileSummary }}
                />
              </GamerFeedbackSummaryTextContainer>
            )}
          </Box>

          {/* Row 2: Personality Traits */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '24px',
              width: '100%'
            }}
          >
            {/* Left: Personality Traits Visualization */}
            {personalityTraits.length > 0 && (
              <PersonalityTraitsVisualization
                traits={personalityTraits}
                title={t('gamer.collaborationProfile.personalityTraits.title')}
              />
            )}

            {/* Right: Self Perceived Personality */}
            {formattedSelfPerceivedPersonality && (
              <GamerFeedbackSummaryTextContainer title={t('gamer.collaborationProfile.selfPerceivedPersonality.title')}>
                <Typography
                  sx={{
                    ...INTERAX_TYPOGRAPHY.body3,
                    color: INTERAX_COLORS.typographyFont,
                    margin: 0,
                    '& strong': {
                      fontWeight: 600,
                      color: INTERAX_COLORS.typographyFont
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: formattedSelfPerceivedPersonality }}
                />
              </GamerFeedbackSummaryTextContainer>
            )}
          </Box>

          {/* Row 3: Achievements Preview */}
          {profileReportData.achievements &&
            (() => {
              const transformedAchievements = transformUserAchievementsToAchievements(profileReportData.achievements)

              // Sort achievements: completed first, then by value
              const sortedAchievements = [...transformedAchievements].sort((a, b) => {
                const aCompleted = isAchievementCompleted(a)
                const bCompleted = isAchievementCompleted(b)
                if (aCompleted !== bCompleted) {
                  return aCompleted ? -1 : 1
                }
                if (a.type === 'tiered' && b.type === 'tiered') {
                  return b.currentValue - a.currentValue
                }
                return 0
              })

              // Take top 4-6 achievements for preview (depending on screen size)
              const previewAchievements = sortedAchievements.slice(0, 6)

              return (
                <Box
                  sx={{
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '30px',
                    gap: '8px',
                    width: '100%',
                    background:
                      'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(6px)',
                    borderRadius: '16px',
                    position: 'relative'
                  }}
                >
                  {/* Title */}
                  {(() => {
                    const titleText = t('gamer.collaborationProfile.achievements.title')
                    const words = titleText.trim().split(/\s+/)
                    const firstWord = words.length > 1 ? words[0] : ''
                    const rest = words.length > 1 ? words.slice(1).join(' ') : words[0] || ''

                    return (
                      <Box
                        sx={{
                          boxSizing: 'border-box',
                          width: '100%',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                          margin: 0
                        }}
                      >
                        {/* First word in white (if multiple words) */}
                        {firstWord && (
                          <Typography
                            sx={{
                              fontFamily: 'Lexend Deca',
                              fontStyle: 'normal',
                              fontWeight: 600,
                              fontSize: '26px',
                              lineHeight: '34px',
                              color: INTERAX_COLORS.white,
                              margin: 0
                            }}
                          >
                            {firstWord}
                          </Typography>
                        )}
                        {/* Rest of name in gradient */}
                        {rest && (
                          <Typography
                            sx={{
                              fontFamily: 'Lexend Deca',
                              fontStyle: 'normal',
                              fontWeight: 600,
                              fontSize: '26px',
                              lineHeight: '34px',
                              background: 'linear-gradient(360deg, #FFD196 -15.91%, #FFAB40 1.14%, #FFFFFF 56.35%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                              textFillColor: 'transparent',
                              textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(255, 171, 64, 0.4)',
                              margin: 0
                            }}
                          >
                            {rest}
                          </Typography>
                        )}
                      </Box>
                    )
                  })()}

                  {/* Achievements Row */}
                  {previewAchievements.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '16px',
                        width: '100%',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        paddingBottom: '8px',
                        // Hide scrollbar but keep functionality
                        scrollbarWidth: 'thin',
                        scrollbarColor: `${INTERAX_COLORS.sun20} transparent`,
                        '&::-webkit-scrollbar': {
                          height: '8px'
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: INTERAX_COLORS.sun20,
                          borderRadius: '4px'
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: INTERAX_COLORS.sun40
                        },
                        // Responsive: wrap on larger screens
                        '@media (min-width: 768px)': {
                          flexWrap: 'wrap',
                          overflowX: 'visible'
                        }
                      }}
                    >
                      {previewAchievements.map((achievement) => (
                        <AchievementBadge key={achievement.id} achievement={achievement} size="small" />
                      ))}
                    </Box>
                  )}

                  {/* View All Button */}
                  <Box
                    sx={{
                      alignSelf: 'flex-end',
                      marginTop: 'auto'
                    }}
                  >
                    <ButtonSecondaryUpgraded variant="gold" onClick={() => navigate(GamerRoutes.ACHIEVEMENTS)}>
                      {t('gamer.collaborationProfile.achievements.viewAll')}
                    </ButtonSecondaryUpgraded>
                  </Box>
                </Box>
              )
            })()}

          {/* Development Plan Sections */}
          {/* What's Working */}
          {whatIsWorkingTabs.length > 0 && (
            <Box id="profile-what-is-working">
              <MyProfileSubSectionCard
                titleKey="gamer.collaborationProfile.whatsWorking.title"
                descriptionKey="gamer.collaborationProfile.whatsWorking.description"
                tabs={whatIsWorkingTabs}
                sectionData={developmentPlanData.whatIsWorking}
                variant="team"
                icon={<LightBulbIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
                {...feedbackCovers.whatsWorking}
              />
            </Box>
          )}

          {/* Watch Out For */}
          {improvementAreasTabs.length > 0 && (
            <Box id="profile-improvement-areas">
              <MyProfileSubSectionCard
                titleKey="gamer.collaborationProfile.improvementAreas.title"
                descriptionKey="gamer.collaborationProfile.improvementAreas.description"
                tabs={improvementAreasTabs}
                sectionData={developmentPlanData.improvementAreas}
                variant="team"
                icon={<LightBulbIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
                {...feedbackCovers.watchOut}
              />
            </Box>
          )}

          {/* Try This At Work */}
          {tryThisAtWorkTabs.length > 0 && (
            <Box id="profile-try-this-at-work">
              <MyProfileSubSectionCard
                titleKey="gamer.collaborationProfile.tryThisAtWork.title"
                descriptionKey="gamer.collaborationProfile.tryThisAtWork.description"
                tabs={tryThisAtWorkTabs}
                sectionData={developmentPlanData.tryThisAtWork}
                variant="team"
                icon={<LightBulbIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
              />
            </Box>
          )}
        </GamerFeedbackContainerBox>
      </Box>
    </PageWrapper>
  )
}

export default MyProfileGamerPage
